import { useNavigate } from "react-router-dom";

import { AppBar } from "../app/Shell";
import { Button, Skeleton } from "../components/primitives";
import { EmptyState } from "../components/feedback";

/** 작업 하나를 여는 동안, 그리고 열지 못했을 때.
 *
 * **"찾을 수 없음"과 뭉개지 않는다.** 불러오는 중인 것과 못 불러온 것과 없는 것은 다른
 * 사실이고, 셋을 한 화면으로 처리하면 작업 열여덟 건을 가진 셀러가 "이 작업을 찾을 수
 * 없습니다"를 본다. `W-11` 이 "왜 없는지 단정하지 않는다"고 적어 둔 것과 같은 성격의
 * 자리다(PRD 7.7.2a).
 *
 * ⚠️ 두 화면 다 목업에 없다. `W-11` 의 껍데기(상단 헤더 + 빈 상태)를 그대로 쓰고, 문구
 * 확정은 design-manager 소관이다. */

/** 여는 중. 헤더에 쓸 상품명·상태가 아직 없어 작업 셸을 세우지 않는다. */
export function JobLoadingScreen() {
  return (
    <div className="wrap">
      <div className="shell">
        <AppBar />
        <div className="panel">
          <div role="status" aria-live="polite">
            <span className="sr-only">작업을 불러오는 중입니다</span>
            <div style={{ display: "grid", gap: 9, maxWidth: 420 }}>
              <Skeleton width="46%" height={14} />
              <Skeleton width="72%" />
              <Skeleton width="58%" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** 못 불러왔다. **없다고 말하지 않는다.** 다시 눌러 볼 수 있게 두는 것이 이 화면의 일이다.
 *
 * **왜 답이 오지 않았는지는 적지 않는다.** 원인을 화면에 적으면 셀러가 할 수 있는 일은 늘지
 * 않고 남의 작업의 유무만 알아낼 수 있게 된다. */
export function JobUnavailableScreen({ onRetry }: { onRetry: () => void }) {
  const navigate = useNavigate();

  return (
    <div className="wrap">
      <div className="shell">
        <AppBar />
        <div className="panel">
          <EmptyState
            art="!"
            tone="bad"
            title="지금 이 작업을 열 수 없어요"
            body={
              <>
                연결이 끊겼거나 잠깐 답이 오지 않았습니다. <b>작업과 사진은 그대로 있습니다.</b>
              </>
            }
            action={
              <div style={{ display: "flex", gap: 8 }}>
                <Button label="내 작업" variant="ghost" onClick={() => navigate("/jobs")} />
                <Button label="다시 시도" onClick={onRetry} />
              </div>
            }
          />
          {/* ⛔ 발밑의 「여기선 아직 못 합니다. 왜 답이 오지 않았는지 보기」 줄을 걷었다
              (2026-10-05 사용자 지시). 셀러가 아니라 설계자에게 하는 말이었다. 원인을 적지
              않는 이유는 이 함수 머리말이 든다. */}
        </div>
      </div>
    </div>
  );
}
